import { HOST_URL } from '../config'
import { HttpStatus } from '../constants/http-status.enum'
import type { ApiResponse } from './index'

export interface IPermission {
    id: number
    key: string
    description?: string | null
    isSystem: boolean
}

export interface IRole {
    id: number
    name: string
    description?: string | null
    isSystem: boolean
}

export interface IUserSession {
    id: string
    userId: number
    tokenPrefix: string
    deviceName?: string | null
    devicePlatform?: string | null
    userAgent?: string | null
    ipAddress?: string | null
    expiresAt: string
    revokedAt?: string | null
    lastSeenAt?: string | null
    createdAt: string
}

export interface IUserMobileDevice {
    id: string
    userId: number
    fingerprint: string
    deviceName?: string | null
    devicePlatform?: string | null
    userAgent?: string | null
    ipAddress?: string | null
    firstSeenAt: string
    lastSeenAt: string
    approvedAt?: string | null
    revokedAt?: string | null
    createdAt: string
    updatedAt: string
}

export interface INotificationItem {
    id: string
    type: string
    title: string
    message?: string | null
    payload?: Record<string, unknown> | null
    isRead: boolean
    createdAt: string
}

export interface INotificationListResult {
    items: INotificationItem[]
    unreadCount: number
}

async function parse<T>(response: Response): Promise<ApiResponse<T>> {
    const result = (await response.json()) as ApiResponse<T>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function getPermissionCatalog(): Promise<ApiResponse<IPermission[]>> {
    return parse(await fetch(`${HOST_URL}permissions/catalog`, { credentials: 'include' }))
}

export async function listRoles(): Promise<ApiResponse<IRole[]>> {
    return parse(await fetch(`${HOST_URL}roles`, { credentials: 'include' }))
}

export async function getRolePermissions(roleId: number): Promise<ApiResponse<string[]>> {
    return parse(await fetch(`${HOST_URL}roles/${roleId}/permissions`, { credentials: 'include' }))
}

export async function createRole(name: string, description?: string): Promise<ApiResponse<IRole>> {
    return parse(
        await fetch(`${HOST_URL}roles`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ name, description }),
        }),
    )
}

export async function updateRole(roleId: number, patch: Partial<Pick<IRole, 'name' | 'description'>>): Promise<ApiResponse<IRole>> {
    return parse(
        await fetch(`${HOST_URL}roles/${roleId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(patch),
        }),
    )
}

export async function deleteRole(roleId: number): Promise<ApiResponse<boolean>> {
    return parse(
        await fetch(`${HOST_URL}roles/${roleId}`, {
            method: 'DELETE',
            credentials: 'include',
        }),
    )
}

export async function replaceRolePermissions(roleId: number, permissionKeys: string[]): Promise<ApiResponse<boolean>> {
    return parse(
        await fetch(`${HOST_URL}roles/${roleId}/permissions`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ permissionKeys }),
        }),
    )
}

export async function cloneRole(roleId: number, name: string): Promise<ApiResponse<IRole>> {
    return parse(
        await fetch(`${HOST_URL}roles/${roleId}/clone`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ name }),
        }),
    )
}

export async function getUserRoles(userId: number): Promise<ApiResponse<number[]>> {
    return parse(await fetch(`${HOST_URL}users/${userId}/roles`, { credentials: 'include' }))
}

export async function replaceUserRoles(userId: number, roleIds: number[]): Promise<ApiResponse<boolean>> {
    return parse(
        await fetch(`${HOST_URL}users/${userId}/roles`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ roleIds }),
        }),
    )
}

export interface IUserPermissionOverride {
    permissionKey: string
    allowed: boolean
}

export async function getUserOverrides(userId: number): Promise<ApiResponse<IUserPermissionOverride[]>> {
    return parse(await fetch(`${HOST_URL}users/${userId}/permission-overrides`, { credentials: 'include' }))
}

export async function replaceUserOverrides(userId: number, overrides: IUserPermissionOverride[]): Promise<ApiResponse<boolean>> {
    return parse(
        await fetch(`${HOST_URL}users/${userId}/permission-overrides`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ overrides }),
        }),
    )
}

export async function listMySessions(): Promise<ApiResponse<IUserSession[]>> {
    const parsed = await parse<any>(await fetch(`${HOST_URL}sessions/me`, { credentials: 'include' }))
    const data = parsed.data
    const rows = Array.isArray(data) ? data : Array.isArray(data?.sessions) ? data.sessions : []
    return { ...parsed, data: rows as IUserSession[] }
}

export async function revokeMySession(sessionId: string): Promise<ApiResponse<boolean>> {
    return parse(
        await fetch(`${HOST_URL}sessions/revoke`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ sessionId }),
        }),
    )
}

export async function revokeOtherSessions(): Promise<ApiResponse<boolean>> {
    return parse(
        await fetch(`${HOST_URL}sessions/revoke-others`, {
            method: 'POST',
            credentials: 'include',
        }),
    )
}

export async function listUserMobileDevices(userId: number): Promise<ApiResponse<IUserMobileDevice[]>> {
    return parse(await fetch(`${HOST_URL}users/${userId}/mobile-devices`, { credentials: 'include' }))
}

export async function listAllMobileDevices(): Promise<ApiResponse<IUserMobileDevice[]>> {
    return parse(await fetch(`${HOST_URL}users/mobile-devices`, { credentials: 'include' }))
}

export async function setUserMobileDeviceApproval(userId: number, deviceId: string, approved: boolean): Promise<ApiResponse<IUserMobileDevice>> {
    return parse(
        await fetch(`${HOST_URL}users/${userId}/mobile-devices/${deviceId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ approved }),
        }),
    )
}

export async function deleteUserMobileDevice(userId: number, deviceId: string): Promise<ApiResponse<boolean>> {
    return parse(
        await fetch(`${HOST_URL}users/${userId}/mobile-devices/${deviceId}`, {
            method: 'DELETE',
            credentials: 'include',
        }),
    )
}

export async function listNotifications(): Promise<ApiResponse<INotificationListResult>> {
    return parse(await fetch(`${HOST_URL}notifications`, { credentials: 'include' }))
}

export async function markNotificationRead(notificationId: string): Promise<ApiResponse<boolean>> {
    return parse(
        await fetch(`${HOST_URL}notifications/${notificationId}/read`, {
            method: 'POST',
            credentials: 'include',
        }),
    )
}

export async function decideDeviceApprovalNotification(notificationId: string, approved: boolean): Promise<ApiResponse<IUserMobileDevice>> {
    return parse(
        await fetch(`${HOST_URL}notifications/${notificationId}/device-approval`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ approved }),
        }),
    )
}
